/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  TablePagination,
  Input,
  FormControl,
  CircularProgress,
} from "@mui/material"
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  HowToVote as HowToVoteIcon,
} from "@mui/icons-material"
import { Cusip } from "../types/api.types"
import { cusipService } from "../services/api/cusipService"
import { useApiState } from "../context/ApiStateContext"
import LoadingOverlay from "../components/LoadingOverlay"
import ConfirmDialog from "../components/ConfirmDialog"
import StatusPopup from "../components/StatusPopup"
import VotingSidebar from "../components/VotingSidebar"

const CusipPage: React.FC = () => {
  const [cusips, setCusips] = useState<Cusip[]>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCusip, setEditingCusip] = useState<Cusip | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [searchQuery, setSearchQuery] = useState("")
  const { loading, setLoading, setError, setSuccess } = useApiState()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [cusipToDelete, setCusipToDelete] = useState<string | null>(null)
  const isFirstRender = useRef(true)
  const [statusPopup, setStatusPopup] = useState<{
    open: boolean
    success: boolean
    message: string
  }>({
    open: false,
    success: false,
    message: "",
  })
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(false)
  const [votingSidebarOpen, setVotingSidebarOpen] = useState(false)
  const [selectedVotingCusip, setSelectedVotingCusip] = useState<Cusip | null>(
    null
  )

  const fetchCusips = async (
    currentPage: number = 0,
    currentPageSize: number = 10,
    search: string = ""
  ) => {
    setLoading(true)
    try {
      const response = await cusipService.getAllCusips(
        search.trim(),
        currentPage + 1,
        currentPageSize
      )
      setCusips(response.data)
      setTotal(response.total)
      setSuccess(true)
    } catch (error) {
      setError(error as any)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (isFirstRender.current) {
      fetchCusips(0, 10)
      isFirstRender.current = false
      return
    }

    if (page > 0 || rowsPerPage !== 10) {
      fetchCusips(page, rowsPerPage)
    }
  }, [page, rowsPerPage])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleAdd = () => {
    setEditingCusip(null)
    setDialogOpen(true)
  }

  const handleEdit = async (cusip: Cusip) => {
    setLoading(true)
    try {
      const updatedCusip = await cusipService.getCusipById(cusip.cusip)
      setEditingCusip(updatedCusip)
      setDialogOpen(true)
    } catch (error) {
      setError(error as any)
    }
    setLoading(false)
  }

  const handleDeleteClick = (cusip: string) => {
    setCusipToDelete(cusip)
    setDeleteDialogOpen(true)
  }

  const showStatusPopup = (success: boolean, message: string) => {
    setStatusPopup({
      open: true,
      success,
      message,
    })
  }

  const handleStatusPopupClose = () => {
    setStatusPopup((prev) => ({ ...prev, open: false }))
  }

  const handleDeleteConfirm = async () => {
    if (!cusipToDelete) return

    setLoading(true)
    try {
      await cusipService.deleteCusip(cusipToDelete)
      showStatusPopup(true, "CUSIP deleted successfully!")
      fetchCusips(page, rowsPerPage)
    } catch (error) {
      showStatusPopup(
        false,
        (error as any)?.message || "Delete operation failed"
      )
    }
    setLoading(false)
    setDeleteDialogOpen(false)
    setCusipToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setCusipToDelete(null)
  }

  const handleExport = async () => {
    setLoading(true)
    try {
      const blob = await cusipService.exportToCsv()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      const guid = crypto.randomUUID()
      const date = new Date().toISOString().split("T")[0]
      a.download = `cusips-${date}-${guid}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      showStatusPopup(true, "Export to CSV completed successfully!")
    } catch (error) {
      showStatusPopup(false, (error as any)?.message || "Export failed")
    }
    setLoading(false)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    const formData = new FormData(event.currentTarget)
    const cusip = formData.get("cusip") as string
    const description = formData.get("description") as string
    const isin = formData.get("isin") as string

    try {
      if (editingCusip) {
        await cusipService.updateCusip(editingCusip.cusip, {
          description,
          isin,
        })
        showStatusPopup(true, "CUSIP updated successfully!")
        // Refresh current page to reflect edits
        fetchCusips(page, rowsPerPage)
      } else {
        await cusipService.createCusip({ cusip, description, isin })
        showStatusPopup(true, "CUSIP created successfully!")
        // Always show newly created CUSIP at the top (first page)
        setPage(0)
        fetchCusips(0, rowsPerPage)
      }
      setDialogOpen(false)
    } catch (error) {
      showStatusPopup(false, (error as any)?.message || "Operation failed")
    }
    setLoading(false)
  }

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      // If search is empty, fetch all CUSIPs
      fetchCusips(page, rowsPerPage)
      return
    }
    setPage(0) // Reset to first page when searching
    fetchCusips(0, rowsPerPage, searchQuery)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch()
    }
  }

  const handleUploadClick = () => {
    setUploadDialogOpen(true)
    setSelectedFile(null)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Check file extension first
      const fileName = file.name.toLowerCase()
      const validExtensions = [".csv", ".xlsx", ".xls"]
      const hasValidExtension = validExtensions.some((ext) =>
        fileName.endsWith(ext)
      )

      if (!hasValidExtension) {
        showStatusPopup(
          false,
          "Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file"
        )
        event.target.value = "" // Clear the input
        return
      }

      // Additional MIME type validation
      const validMimeTypes = [
        "text/csv",
        "application/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ]

      if (!validMimeTypes.includes(file.type) && file.type !== "") {
        showStatusPopup(
          false,
          "Invalid file type. Please select a valid Excel or CSV file"
        )
        event.target.value = "" // Clear the input
        return
      }

      setSelectedFile(file)
    }
  }

  const handleUploadConfirm = async () => {
    if (!selectedFile) {
      showStatusPopup(false, "Please select a file to upload")
      return
    }

    setUploadProgress(true)
    try {
      await cusipService.uploadCusips(selectedFile)
      showStatusPopup(true, "CUSIPs uploaded successfully!")
      setUploadDialogOpen(false)
      setSelectedFile(null)
      // Refresh the CUSIP list so newly added rows appear at the top
      setPage(0)
      fetchCusips(0, rowsPerPage)
    } catch (error: any) {
      let errorMessage = "Upload failed"

      if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === "string") {
        errorMessage = error
      }

      showStatusPopup(false, errorMessage)
    }
    setUploadProgress(false)
  }

  const handleUploadCancel = () => {
    setUploadDialogOpen(false)
    setSelectedFile(null)
  }

  const handleDownloadTemplate = async () => {
    setLoading(true)
    try {
      const blob = await cusipService.downloadTemplate()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "cusip-template.xlsx"
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      showStatusPopup(true, "Template downloaded successfully!")
    } catch (error) {
      showStatusPopup(
        false,
        (error as any)?.message || "Template download failed"
      )
    }
    setLoading(false)
  }

  const handleVoteNow = (cusip: Cusip) => {
    setSelectedVotingCusip(cusip)
    setVotingSidebarOpen(true)
  }

  const handleVotingSidebarClose = () => {
    setVotingSidebarOpen(false)
    setSelectedVotingCusip(null)
  }

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: 2,
        p: { xs: 2, sm: 3 },
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <Box sx={{ p: 0 }}>
        <LoadingOverlay open={loading} />

        {/* Status Popup */}
        <StatusPopup
          open={statusPopup.open}
          success={statusPopup.success}
          message={statusPopup.message}
          onClose={handleStatusPopupClose}
        />

        {/* Header Section */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              CUSIP Management
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Manage and organize your CUSIP data efficiently
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleUploadClick}
              startIcon={<CloudUploadIcon />}
              sx={{ borderRadius: 2 }}
            >
              Upload
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExport}
              sx={{ borderRadius: 2 }}
            >
              Export to CSV
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              }}
            >
              Add CUSIP
            </Button>
          </Box>
        </Box>

        {/* Search Section */}
        <Box
          sx={{
            mb: 3,
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search CUSIPs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              minWidth: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Search
          </Button>
        </Box>

        {/* Table Section */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 600 }}>CUSIP</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>ISIN</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Vote
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cusips.map((cusip, index) => (
                  <TableRow
                    key={cusip.cusip}
                    sx={{
                      "&:hover": { bgcolor: "#f8f9fa" },
                      "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {cusip.cusip}
                    </TableCell>
                    <TableCell>{cusip.description}</TableCell>
                    <TableCell>{cusip.isin}</TableCell>
                    <TableCell align="center">
                      <Button
                        onClick={() => handleVoteNow(cusip)}
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<HowToVoteIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          px: 2,
                          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        }}
                      >
                        Vote Now
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleEdit(cusip)}
                        color="primary"
                        sx={{
                          mr: 1,
                          "&:hover": { bgcolor: "rgba(25, 118, 210, 0.1)" },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(cusip.cusip)}
                        color="error"
                        sx={{
                          "&:hover": { bgcolor: "rgba(211, 47, 47, 0.1)" },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              bgcolor: "#f8f9fa",
            }}
          />
        </Box>

        {/* Add/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 },
          }}
        >
          <form onSubmit={handleSubmit}>
            <DialogTitle
              sx={{
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#f8f9fa",
              }}
            >
              {editingCusip ? "Edit CUSIP" : "Add CUSIP"}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  autoFocus
                  name="cusip"
                  label="CUSIP"
                  type="text"
                  fullWidth
                  required
                  disabled={!!editingCusip}
                  defaultValue={editingCusip?.cusip}
                  inputProps={{
                    pattern: "[A-Z0-9]{9}",
                    title: "CUSIP must be 9 alphanumeric characters",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                    },
                  }}
                />
                <TextField
                  name="description"
                  label="Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={4}
                  defaultValue={editingCusip?.description}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                    },
                  }}
                />
                <TextField
                  name="isin"
                  label="ISIN"
                  type="text"
                  fullWidth
                  defaultValue={editingCusip?.isin}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 2 }}>
              <Button
                onClick={() => setDialogOpen(false)}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                }}
              >
                {editingCusip ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete CUSIP"
          message={`Are you sure you want to delete CUSIP ${cusipToDelete}?`}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />

        {/* Upload Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={handleUploadCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              pb: 1,
              borderBottom: "1px solid #e0e0e0",
              bgcolor: "#f8f9fa",
            }}
          >
            Upload CUSIPs
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Instructions */}
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                  Please select an Excel (.xlsx, .xls) or CSV file to upload
                  CUSIP data.
                </Typography>
              </Box>

              {/* File Upload Section */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Choose File
                </Typography>
                <FormControl fullWidth>
                  <Input
                    id="file-input"
                    type="file"
                    inputProps={{
                      accept: ".csv,.xlsx,.xls",
                    }}
                    onChange={handleFileSelect}
                  />
                </FormControl>
                {selectedFile && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#4caf50", textAlign: "center" }}
                  >
                    Selected file: {selectedFile.name}
                  </Typography>
                )}
              </Box>

              {/* Download Template Section */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleDownloadTemplate}
                  disabled={loading}
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    minWidth: 200,
                    borderRadius: 2,
                  }}
                >
                  Download Template
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
            <Button
              onClick={handleUploadCancel}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderRadius: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadConfirm}
              variant="contained"
              color="primary"
              disabled={!selectedFile || uploadProgress}
              startIcon={uploadProgress ? <CircularProgress size={20} /> : null}
              sx={{
                minWidth: 100,
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              }}
            >
              {uploadProgress ? "Uploading..." : "Upload"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Voting Sidebar */}
        <VotingSidebar
          open={votingSidebarOpen}
          onClose={handleVotingSidebarClose}
          cusip={selectedVotingCusip?.cusip || ""}
          description={selectedVotingCusip?.description}
        />
      </Box>
    </Box>
  )
}

export default CusipPage
